import { useContext, useEffect, useRef } from 'react';
import AnalyticsContext, { AnalyticsContextType } from '@/contexts/analytics/analytics-context';
import { currentPathName, getPathStep } from '@/services/location-service';
import { EventProperties, trackEvent } from '@/services/analytics-service';
import { FlowStatus } from './use-flow';
import { SceneData } from '@/schemas/sceneDataSchema';

interface QuoteError {
  data?: unknown;
  status?: number;
}

interface UseErrorPageAnalyticsParams {
  flowStatus: FlowStatus;
  quoteError: QuoteError | null;
  sceneData?: SceneData;
  errorStatus?: number | null;
}

export default function useErrorPageAnalytics({
  flowStatus,
  quoteError,
  sceneData,
  errorStatus,
}: UseErrorPageAnalyticsParams) {
  const {
    eventPrefix,
    context: { quoteId, agentId, publicId, experience },
  } = useContext<AnalyticsContextType>(AnalyticsContext);
  const hasTrackedErrorDisplay = useRef(false);

  useEffect(() => {
    const isErrorDisplayed = flowStatus === 'error' || !!quoteError;

    if (!isErrorDisplayed) {
      hasTrackedErrorDisplay.current = false;
      return;
    }

    if (hasTrackedErrorDisplay.current) {
      return;
    }

    hasTrackedErrorDisplay.current = true;

    const { step_id, entity_index } = sceneData
      ? { step_id: sceneData.step_id, entity_index: sceneData.entity_index }
      : getPathStep(currentPathName());
    const errorCategory = errorStatus === 401 ? 'UNAUTHORIZED' : quoteError ? 'QUOTE_ERROR' : 'GENERAL';
    const eventProperties: EventProperties = {
      eventPrefix,
      quoteId: sceneData?.quote_id ?? quoteId,
      agentId,
      publicId,
      experience: sceneData?.experience ?? experience,
      stepId: step_id ?? null,
      entityIndex: entity_index != null ? String(entity_index) : null,
      errorCategory,
    };

    trackEvent('ERROR_PAGE', 'DISPLAYED', eventProperties, sceneData?.quote_id ?? quoteId);
  }, [agentId, errorStatus, eventPrefix, flowStatus, publicId, quoteError, sceneData, experience, quoteId]);
}
